import { graphHooks, useConflictResolutionKey } from '@tunasong/graph-lib/react'
import { Share } from '@tunasong/icons'
import type { RenderMenuItemProps } from '@tunasong/plugin-lib'
import type { Poll } from '@tunasong/schemas'
import { isPersisted, isPoll } from '@tunasong/schemas'
import {
  ConfirmDialog,
  EntityConflictBanner,
  UserList,
  ListItemIcon,
  ListItemText,
  MenuItem as MuiMenuItem,
} from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useCallback, useMemo, useState } from 'react'
import invariant from 'tiny-invariant'

/** The held field the invite dialog shows (GH #1912) */
const invitedFields = ['invited'] as const
/** Action to create playlist from songs and folders */
const InviteAction: FC<RenderMenuItemProps> = props => {
  const { element, onClose, ...restProps } = props

  const updateEntity = graphHooks.useEntityUpdate<Poll>()
  const { profile } = graphHooks.useProfile()

  const defaultInvited = isPoll(element) && element.invited ? element.invited : []
  const [invited, setInvited] = useState<string[]>(defaultInvited)

  /**
   * "Use their version" and its Undo replace the poll's `invited` under the ticks I made here; show the poll's again,
   * or Confirm would save mine back over theirs (GH #1912).
   */
  const resolution = useConflictResolutionKey(element.id)
  const [shownResolution, setShownResolution] = useState(resolution)
  if (resolution !== shownResolution) {
    setShownResolution(resolution)
    setInvited(defaultInvited)
  }

  const [showConfirm, setShowConfirm] = useState(false)
  const handleShowConfirm = useCallback(() => setShowConfirm(true), [])
  const acls = isPersisted(element) ? element.acls : undefined

  const handleCancel = useCallback(
    (ev: React.SyntheticEvent<Element, Event>) => {
      ev.stopPropagation()
      setShowConfirm(false)
      if (onClose) {
        onClose()
      }
    },
    [onClose]
  )

  const handleCreate = useCallback(
    async (ev: React.SyntheticEvent<Element, Event>) => {
      ev.preventDefault()
      invariant(isPersisted(element), 'Element must be persisted')
      updateEntity(element.id, { invited })
    },
    [element, invited, updateEntity]
  )

  const principals = useMemo(
    () => acls?.map(a => a.principal).filter(p => p !== profile?.userId) ?? [],
    [acls, profile]
  )

  if (!(isPoll(element) && isPersisted(element))) {
    return null
  }

  return (
    <MuiMenuItem {...restProps} onClick={handleShowConfirm}>
      <ListItemIcon>
        <Share />
      </ListItemIcon>
      <ListItemText>Invite to poll</ListItemText>
      <ConfirmDialog
        open={showConfirm}
        maxWidth={'sm'}
        fullWidth={true}
        title="Invite to poll"
        text={'Invite users to poll'}
        onCancel={handleCancel}
        onConfirm={handleCreate}
      >
        {/* GH #1912: above the user list */}
        <EntityConflictBanner entityId={element.id} fields={invitedFields} />
        <UserList userIds={principals} checked={invited} onChange={setInvited} />
      </ConfirmDialog>
    </MuiMenuItem>
  )
}

export default InviteAction
